/**
 * Gedeelde winkelmandstatus (SHO-10074).
 *
 * Zeven plekken in het thema haalden los van elkaar `/cart.js` op. Bij het laden van een
 * PDP leverde dat drie identieke calls binnen 400 ms: `staalkiezer.js` en twee keer
 * `mixed-offer.js`. Ze willen alle drie hetzelfde weten — wat ligt er nu in de mand.
 *
 * Deze module geeft één antwoord aan iedereen:
 *
 * - **single-flight** — wie meelift op een lopende call krijgt dezelfde promise;
 * - **korte houdbaarheid** — een antwoord blijft `HOUDBAARHEID_MS` bruikbaar, net lang
 *   genoeg om een reeks bij het laden op te vangen;
 * - **leeglopen bij wijziging** — elk `cart:update`/`cart:error` gooit het antwoord weg,
 *   dus na een toevoeging of aanpassing haalt de volgende aanroeper verse data.
 *
 * Wie zelf net de mand heeft gewijzigd vraagt `{ vers: true }` en slaat de cache over.
 *
 * Classic scripts (zoals `mixed-offer.js`) kunnen niet importeren en gebruiken
 * `window.HdsCartState`; die wijst naar dezelfde staat als de import.
 */

/** Hoe lang een opgehaalde mand hergebruikt mag worden. */
import { whenPageActive } from '@theme/hds-page-activation';

const HOUDBAARHEID_MS = 2000;

/** @type {object | null} */
let bewaard = null;
let bewaardOp = 0;
/** @type {Promise<object> | null} */
let lopend = null;

function cartUrl() {
  const root = window.Shopify?.routes?.root ?? '/';
  return `${root}cart.js`;
}

function haalOp() {
  return fetch(cartUrl(), {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  }).then((response) => {
    if (!response.ok) throw new Error(`cart.js HTTP ${response.status}`);
    return response.json();
  });
}

/**
 * De huidige winkelmand.
 *
 * @param {{ vers?: boolean }} [opties] - `vers: true` negeert de cache; gebruik dat direct
 *   na een eigen wijziging aan de mand.
 * @returns {Promise<object>}
 */
export function getCart({ vers = false } = {}) {
  if (document.prerendering) {
    return new Promise((resolve, reject) => whenPageActive(() => getCart({ vers }).then(resolve, reject)));
  }
  if (!vers) {
    if (bewaard && Date.now() - bewaardOp < HOUDBAARHEID_MS) return Promise.resolve(bewaard);
    if (lopend) return lopend;
  }

  const verzoek = haalOp()
    .then((cart) => {
      rememberCart(cart);
      return cart;
    })
    .finally(() => {
      // Alleen opruimen als dit nog onze eigen vlucht is; een `vers`-aanroeper kan er
      // inmiddels een nieuwere hebben gezet.
      if (lopend === verzoek) lopend = null;
    });

  lopend = verzoek;
  return verzoek;
}

/** Bewaar een mand die we net binnen hebben, zodat een volgende lezer hem hergebruikt. */
export function rememberCart(cart) {
  bewaard = cart;
  bewaardOp = Date.now();
}

/** Gooi de bewaarde mand weg; de volgende lezer haalt verse data op. */
export function forgetCart() {
  bewaard = null;
  bewaardOp = 0;
}

for (const naam of ['cart:update', 'cart:error']) {
  document.addEventListener(naam, forgetCart);
  window.addEventListener(naam, forgetCart);
}

document.addEventListener('prerenderingchange', forgetCart, { once: true });
document.addEventListener('cart:update', () => {
  try { localStorage.setItem('hds:cart-version', String(Date.now())); } catch {}
});
window.addEventListener('storage', (event) => {
  if (event.key === 'hds:cart-version') forgetCart();
});

// Terug via de terugknop kan een mand tonen die inmiddels ergens anders is gewijzigd.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) forgetCart();
});

window.HdsCartState = { getCart, rememberCart, forgetCart };
